import { defineComponent as _defineComponent } from 'vue'
import { computed, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'UIRangeSlider',
  props: {
    low: null,
    high: null,
    min: { default: 0 },
    max: { default: 100 },
    step: { default: 1 },
    minGap: { default: 1 },
    parentHasGroup: { type: Boolean, default: false }
  },
  emits: ["update:low", "update:high"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'update:low', value: number): void
  (e: 'update:high', value: number): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { low: number, high: number, min: number, max: number, step: number, minGap: number, parentHasGroup: boolean };





// Use provided minGap, or default to step size
const actualMinGap = computed(() => props.minGap ?? props.step)
const range = computed(() => props.max - props.min)

// Determine precision based on step value
const stepPrecision = computed(() => {
  const stepStr = props.step.toString()
  return stepStr.includes('.') ? stepStr.split('.')[1].length : 0
})

// Writable computed properties to link v-model with props/emits
const localLow = computed({
  get: () => props.low,
  set: (value) => {
    if (!isNaN(value)) {
      const clampedValue = Math.max(props.min, Math.min(props.max, value))
      emits('update:low', Number(clampedValue.toFixed(stepPrecision.value)))
    }
  }
})

const localHigh = computed({
  get: () => props.high,
  set: (value) => {
    if (!isNaN(value)) {
      const clampedValue = Math.max(props.min, Math.min(props.max, value))
      emits('update:high', Number(clampedValue.toFixed(stepPrecision.value)))
    }
  }
})

// Watch for changes and enforce minimum gap
watch(
  [() => props.low, () => props.high],
  ([newLow, newHigh], [oldLow, oldHigh]) => {
    // Skip validation if initial run or invalid values
    const isInitialRun = oldLow === undefined || oldHigh === undefined
    if (isInitialRun) return

    const currentLow = Number(newLow)
    const currentHigh = Number(newHigh)
    const previousLow = Number(oldLow)
    const previousHigh = Number(oldHigh)

    // Skip if any values are NaN
    if (isNaN(currentLow) || isNaN(currentHigh) || isNaN(previousLow) || isNaN(previousHigh)) return

    const gap = currentHigh - currentLow

    // If gap violates the minimum gap requirement, revert changed value(s)
    // Using full actualMinGap rather than half for more intuitive behavior
    if (gap < actualMinGap.value) {
      const lowChanged = currentLow !== previousLow
      const highChanged = currentHigh !== previousHigh

      if (lowChanged && !highChanged) {
        emits('update:low', Number(previousLow.toFixed(stepPrecision.value)))
      } else if (!lowChanged && highChanged) {
        emits('update:high', Number(previousHigh.toFixed(stepPrecision.value)))
      } else if (lowChanged && highChanged) {
        emits('update:low', Number(previousLow.toFixed(stepPrecision.value)))
        emits('update:high', Number(previousHigh.toFixed(stepPrecision.value)))
      }
    }
  },
  { immediate: true }
)

// Formats value by clamping to min/max and aligning to nearest step
function formatValue(
  value: number | string | null | undefined,
  currentMin: number,
  currentMax: number,
  step: number
): number {
  let numValue = typeof value === 'string' ? parseFloat(value) : Number(value)
  if (isNaN(numValue)) {
    numValue = currentMin
  }

  const clampedValue = Math.max(currentMin, Math.min(currentMax, numValue))
  const effectiveStep = Math.max(Number.MIN_VALUE, step)
  const steps = Math.round((clampedValue - currentMin) / effectiveStep)
  let steppedValue = currentMin + steps * effectiveStep
  const precision = Math.max(
    (effectiveStep.toString().split('.')[1] || '').length,
    (currentMin.toString().split('.')[1] || '').length
  )
  steppedValue = parseFloat(steppedValue.toFixed(precision))
  // Reuse variable for final clamp
  return Math.max(currentMin, Math.min(currentMax, steppedValue))
}

// Handle direct input in number fields
const handleInput = (which: 'low' | 'high', event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value

  if (value === '-' || value.endsWith('.') || value === '') {
    return
  }

  const numericValue = parseFloat(value)
  if (!isNaN(numericValue)) {
    // Visually clamp only
    if (numericValue > props.max) {
      target.value = props.max.toString()
    } else if (numericValue < props.min) {
      target.value = props.min.toString()
    }
  }
}

// Handle blur event for number fields
const handleBlur = (which: 'low' | 'high', event: Event) => {
  const target = event.target as HTMLInputElement
  let formattedValue: number

  if (target.value === '' || isNaN(parseFloat(target.value))) {
    formattedValue = props[which]
  } else {
    formattedValue = formatValue(target.value, props.min, props.max, props.step)
  }

  target.value = formattedValue.toString()

  if (which === 'low') {
    if (formattedValue !== props.low) {
      emits('update:low', Number(formattedValue.toFixed(stepPrecision.value)))
    }
  } else {
    if (formattedValue !== props.high) {
      emits('update:high', Number(formattedValue.toFixed(stepPrecision.value)))
    }
  }
}

// --- Style Calculation Computeds ---
const calculatePercentage = (value: number) => {
  if (isNaN(value) || range.value <= 0) return 0
  return Math.max(0, Math.min(100, ((value - props.min) / range.value) * 100))
}

// Calculate percentages once and reuse them
const lowPercentage = computed(() => calculatePercentage(props.low))
const highPercentage = computed(() => calculatePercentage(props.high))

const rangeTrackStyle = computed(() => ({
  left: `${lowPercentage.value}%`,
  right: `${100 - highPercentage.value}%`
}))

const minValueStyle = computed(() => ({
  left: `${lowPercentage.value}%`,
  transform: 'translateX(-50%) translateY(-100%)'
}))

const maxValueStyle = computed(() => ({
  left: `${highPercentage.value}%`,
  transform: 'translateX(-50%) translateY(100%)'
}))

return { __sfc: true,props, emits, actualMinGap, range, stepPrecision, localLow, localHigh, formatValue, handleInput, handleBlur, calculatePercentage, lowPercentage, highPercentage, rangeTrackStyle, minValueStyle, maxValueStyle }
}

})